import { IconEdit, IconTrash } from "@tabler/icons-react";
import { inventoryApi } from "apis/inventoryApi";
import CustomTable from "components/ui/CustomTable";
import CustomText from "components/ui/CustomText/CustomText";
import GroupActions from "components/ui/GroupActions";
import { ActionItem } from "components/ui/GroupActions/GroupActions";
import {
  InventoryParamsType,
  InventoryResponseType,
} from "constants/types/inventory";
import { MRT_ColumnDef, MRT_ColumnFiltersState } from "mantine-react-table";
import { formatCurrency } from "utils/helpers/format";
import { FORM_IDS } from "pages/Inventory/config";
import { FC, useMemo, useRef, useCallback } from "react";
import { useNotification } from "utils/hooks";
import {
  useInventories,
  getInventoryListKey,
} from "utils/hooks/request/useInventories";
import { useAppModal } from "utils/hooks/ui/useAppModal";
import { useSWRConfig } from "swr";
import { Loading } from "components";
import EditForm from "../EditForm/EditForm";
import { useAbility } from "configs/casl";
import { canAccessRequirement } from "utils/helpers/authorization";

interface InventoryTableProps {
  params: InventoryParamsType;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const ICON_SIZE = 18;

const InventoryTable: FC<InventoryTableProps> = ({
  params,
  onPageChange,
  onLimitChange,
}) => {
  const { mutate } = useSWRConfig();
  const ability = useAbility();

  const {
    inventories,
    pagination,
    isLoading,
    mutate: localMutate,
  } = useInventories(params);

  const { openCrudModal, closeAll, closeCrudModal } = useAppModal();
  const activeModalId = useRef<string | null>(null);

  const handleCloseModal = useCallback(() => {
    if (activeModalId.current) {
      closeCrudModal(activeModalId.current);
      activeModalId.current = null;
    }
  }, [closeCrudModal]);

  const handleOpenEditModal = useCallback(
    (inventory: InventoryResponseType) => {
      const modalId = openCrudModal(
        <CustomText variant="section-title">Chỉnh sửa tồn kho</CustomText>,
        {
          mode: "edit",
          formId: FORM_IDS.EDIT,
          children: (
            <EditForm
              inventory={inventory}
              mutate={() => mutate(getInventoryListKey(params))}
              onClose={handleCloseModal}
            />
          ),
          submitText: "Cập nhật",
        },
        400,
      );
      activeModalId.current = modalId;
    },
    [openCrudModal, mutate, params, handleCloseModal],
  );

  const inventoryActions = useMemo(
    (): ActionItem<InventoryResponseType>[] => [
      {
        key: "edit",
        label: "Chỉnh sửa",
        icon: <IconEdit size={ICON_SIZE} />,
        color: "orange",
        permission: "inventory:update",
        onClick: (row) => handleOpenEditModal(row),
      },
    ],
    [handleOpenEditModal],
  );

  const hasActions = useMemo(
    () =>
      inventoryActions.some((a) =>
        canAccessRequirement(ability, a.permission, true),
      ),
    [inventoryActions, ability],
  );

  const columns: MRT_ColumnDef<InventoryResponseType>[] = [
    {
      id: "stt",
      header: "STT",
      size: 5,
      grow: false,
      Cell: ({ row }) => row.index + 1,
      mantineTableHeadCellProps: { align: "center" },
      mantineTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "product.name",
      header: "Mặt hàng",
    },
    {
      accessorKey: "product.code",
      header: "Mã mặt hàng",
    },
    {
      accessorKey: "warehouse.name",
      header: "Kho",
    },
    {
      accessorKey: "product.category.name",
      header: "Nhóm mặt hàng",
    },
    {
      accessorKey: "product.priceNet",
      header: "Giá Net",
      size: 140,
      grow: false,
      mantineTableHeadCellProps: { align: "center" },
      mantineTableBodyCellProps: { align: "right" },
      Cell: ({ row }) => formatCurrency(row.original.product?.priceNet ?? 0),
    },
    {
      accessorKey: "quantity",
      header: "Số lượng",
    },
    ...(hasActions
      ? [
          {
            id: "actions",
            header: "Thao tác",
            enableSorting: false,
            enableColumnFilter: false,
            size: 50,
            grow: false,
            enableResizing: false,
            mantineTableHeadCellProps: { align: "center" },
            mantineTableBodyCellProps: { align: "center" },
            Cell: ({ row }: any) => (
              <GroupActions row={row.original} actions={inventoryActions} />
            ),
          } satisfies MRT_ColumnDef<InventoryResponseType>,
        ]
      : []),
  ];

  const handleChange = useCallback(
    (state: {
      page: number;
      limit: number;
      filters?: MRT_ColumnFiltersState;
    }) => {
      if (pagination && state.page !== pagination.page) {
        onPageChange(state.page);
      } else if (pagination && state.limit !== pagination.limit) {
        onLimitChange(state.limit);
      }
    },
    [onPageChange, onLimitChange, pagination],
  );

  return (
    <Loading variant="overlay" visible={isLoading}>
      <CustomTable
        columns={columns}
        data={inventories ?? []}
        pagination={pagination!}
        onStateChange={handleChange}
      />
    </Loading>
  );
};

export default InventoryTable;
